import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, V1 } from "@/lib/api";
import { Sparkles, Ticket, Infinity as InfIcon, Package, Calendar, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import PurchaseButton from "@/components/PurchaseButton";
import { useAuth } from "@/lib/auth";

const MODULE_ICON = {
  soru_cozumu: "💡",
  ai_rapor: "📊",
  tercih_danismanligi: "🎯",
  premium_denemeler: "🎫",
};

export default function MySubscription() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [credits, setCredits] = useState(0);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    if (!user?.role) return;
    api.get(`${V1}/users/me/usage`).then((r) => setData(r.data));
    api.get(`${V1}/users/me/credits`).then((r) => setCredits(r.data?.exam_credits ?? 0));
    api.get(`${V1}/subscription-plans`, { params: { audience: user.role } }).then((r) => setPlans((r.data || []).filter((p) => p.price_monthly > 0)));
  }, [user?.role]);

  if (!data) return <div className="text-muted-foreground">Yükleniyor…</div>;

  const { subscription, plan, modules, month } = data;
  const active = !!subscription;
  const daysLeft = active
    ? Math.max(0, Math.ceil((new Date(subscription.expires_at) - new Date()) / 86400000))
    : 0;

  return (
    <div className="space-y-6" data-testid="my-subscription-page">
      <div>
        <div className="overline text-accent">Aboneliğim</div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2">
          <Package className="h-7 w-7 text-primary" /> Modül Kullanımım
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Bu ay ({month}) her modülde kaç adet kullandığını gör, kalan kotanı ve aboneliğinin bitiş tarihini takip et.
        </p>
      </div>

      <div className={`p-6 rounded-2xl border ${active ? "border-accent/40 bg-gradient-to-br from-card to-accent/5" : "border-border bg-card"}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline text-accent flex items-center gap-1">
              {active ? <><Sparkles className="h-3 w-3" /> Aktif Abonelik</> : "Ücretsiz Plan"}
            </div>
            <div className="font-display text-3xl font-bold mt-1">
              {plan?.name || "Başlangıç"}
              {active && plan?.price_monthly > 0 && (
                <span className="text-base font-normal text-muted-foreground ml-2">₺{plan.price_monthly}/ay</span>
              )}
            </div>
            {plan?.description && <p className="text-sm text-muted-foreground mt-1 max-w-xl">{plan.description}</p>}
          </div>
          {active && (
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Kalan Süre</div>
              <div className="text-2xl font-display font-bold flex items-center gap-1 justify-end">
                <Calendar className="h-5 w-5" /> {daysLeft} gün
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {new Date(subscription.expires_at).toLocaleDateString("tr-TR")}&apos;ye kadar
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {modules.map((m) => (
          <ModuleCard key={m.module.key} item={m} />
        ))}
      </div>

      <div className="p-6 rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-display font-bold text-lg flex items-center gap-2">
              <Ticket className="h-5 w-5 text-accent" /> Kredi Bakiyen: <span className="text-accent">{credits}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Aboneliğin bitince veya kotan dolarsa modülleri kredi ile kullanmaya devam edebilirsin.
            </p>
          </div>
          <Button asChild variant="outline" data-testid="earn-credits-link">
            <Link to="/panel/referans"><Gift className="h-4 w-4" /> Arkadaş Davet Et</Link>
          </Button>
        </div>
      </div>

      {plans.length > 0 && (
        <div>
          <div className="overline text-accent mb-2">Plan Yükselt</div>
          <h2 className="font-display text-2xl font-bold mb-4">Aylık veya yıllık plan al</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {plans.map((p) => (
              <div key={p.code} className={`p-5 rounded-xl border ${p.highlight ? "border-accent/40 bg-accent/5" : "border-border bg-card"}`} data-testid={`upgrade-plan-${p.code}`}>
                <div className="overline text-accent">{p.name}</div>
                <div className="font-display text-3xl font-bold mt-1">₺{p.price_monthly}<span className="text-sm font-normal text-muted-foreground">/ay</span></div>
                {p.price_yearly > 0 && <div className="text-xs text-muted-foreground">Yıllık: ₺{p.price_yearly}</div>}
                <p className="text-sm text-muted-foreground mt-2 min-h-[40px]">{p.description}</p>
                <div className="mt-4 flex flex-col gap-2">
                  <PurchaseButton kind="plan" code={p.code} cycle="monthly" price={p.price_monthly} label={`Aylık ₺${p.price_monthly}`} testId={`buy-plan-monthly-${p.code}`} />
                  {p.price_yearly > 0 && (
                    <PurchaseButton kind="plan" code={p.code} cycle="yearly" price={p.price_yearly} label={`Yıllık ₺${p.price_yearly}`} variant="outline" testId={`buy-plan-yearly-${p.code}`} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const ModuleCard = ({ item }) => {
  const enabled = item.module?.enabled;
  if (item.unlimited) {
    return (
      <div className="p-5 rounded-xl border border-success/40 bg-success/5" data-testid={`sub-mod-${item.module.key}`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-2xl">{MODULE_ICON[item.module.key] || "🧩"}</span>
          <div className="font-display font-bold text-lg">{item.label}</div>
          <InfIcon className="h-4 w-4 text-success ml-auto" />
        </div>
        <div className="text-sm text-success font-medium">Sınırsız</div>
        <div className="text-xs text-muted-foreground mt-1">
          Bu ay <b>{item.used}</b> kullanım — kota yok, dilediğin kadar kullanabilirsin.
        </div>
      </div>
    );
  }
  if (item.quota != null) {
    const pct = Math.min(100, Math.round((item.used / item.quota) * 100));
    const done = item.remaining === 0;
    return (
      <div className="p-5 rounded-xl border border-border bg-card" data-testid={`sub-mod-${item.module.key}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-2xl">{MODULE_ICON[item.module.key] || "🧩"}</span>
          <div className="font-display font-bold text-lg">{item.label}</div>
          <div className="ml-auto text-sm font-mono">
            <b>{item.used}</b> / {item.quota}
          </div>
        </div>
        <div className="h-2 bg-muted rounded overflow-hidden">
          <div
            className={`h-full transition-all duration-700 ${done ? "bg-destructive" : pct > 80 ? "bg-amber-500" : "bg-accent"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className={`mt-2 text-xs ${done ? "text-destructive" : "text-muted-foreground"}`}>
          {done ? (
            <>Bu ayki kotanı doldurdun. Modül şimdi <b>{item.module.credit_cost} kredi</b> ile kullanılabilir.</>
          ) : (
            <>Bu ay <b>{item.remaining}</b> kullanım hakkın kaldı.</>
          )}
        </div>
      </div>
    );
  }
  // Credit only
  return (
    <div className={`p-5 rounded-xl border ${enabled ? "border-border" : "border-muted opacity-60"} bg-card`} data-testid={`sub-mod-${item.module.key}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-2xl">{MODULE_ICON[item.module.key] || "🧩"}</span>
        <div className="font-display font-bold text-lg">{item.label}</div>
        <div className="ml-auto text-sm font-mono flex items-center gap-1 text-accent">
          <Ticket className="h-3.5 w-3.5" /> {item.credit_cost} kredi
        </div>
      </div>
      <div className="text-xs text-muted-foreground">
        {enabled ? (
          <>Bu ay <b>{item.used}</b> kullanım. Her kullanım {item.credit_cost} kredi.</>
        ) : (
          "Bu modül şu an kapalı."
        )}
      </div>
    </div>
  );
};
